import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { ConfirmOptions, setConfirmRequester } from "../utils/confirm";

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/**
 * Renders confirmation dialogs raised anywhere in the app, replacing
 * the blocking window.confirm() calls this codebase relied on.
 */
const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  // Held in a ref so unmounting resolves the promise instead of
  // leaving the caller awaiting forever.
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const request = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    [],
  );

  useEffect(() => {
    setConfirmRequester(request);
    return () => {
      setConfirmRequester(null);
      pendingRef.current?.resolve(false);
    };
  }, [request]);

  const settle = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  return (
    <>
      {children}
      <Dialog
        open={!!pending}
        onClose={() => settle(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{pending?.title ?? "Sind Sie sicher?"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {pending?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => settle(false)}>
            {pending?.cancelLabel ?? "Abbrechen"}
          </Button>
          <Button
            variant="contained"
            color={pending?.destructive ? "error" : "primary"}
            onClick={() => settle(true)}
            autoFocus
          >
            {pending?.confirmLabel ?? "Bestätigen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmProvider;
